import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  MoreVertical,
  Edit2,
  Trash2,
  Heart,
  Users,
  Calendar,
  Info,
  Plus, // Import Plus icon for Add button
} from "lucide-react";
import DataTable from "react-data-table-component";
import Header from "../Components/Header";
import { leadsStyles } from "../utils/customStyles";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  addDoc, // Import addDoc for adding new documents
  serverTimestamp, // Import serverTimestamp for createdAt field
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import LeadInfoModal from "../Components/LeadInfoModal";
import DateIcon from "../Images/date_svgrepo.png";
import SourceIcon from "../Images/source_svgrepo.png";
import StatusIcon from "../Images/status_svgrepo.png";
import BudgetIcon from "../Images/dollar_svgrepo.png";
import MessageIcon from "../Images/message-svgrepo.png";
import UserIcon from "../Images/user_svgrepo.png";
import AddLeadModal from "../Components/AddLeadModal";


const statusOptions = [
  "à contacter",
  "closé",
  "à rappeler",
  "pas intéressé",
  "Faux numéro",
  "rdv booké",
  "follow-up",
  "no show",
];

const Leads = ({ setIsSidebarOpen, isSidebarOpen }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [isAddLeadModalOpen, setIsAddLeadModalOpen] = useState(false); // New state for add lead modal

  // Placeholder for current user ID (replace with actual user authentication logic)
  const currentUserId = auth.currentUser.uid; // Replace with actual user ID from auth context

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "kanban_tasks"),
      (snapshot) => {
        const fetched = snapshot.docs.map((doc) => {
          const data = doc.data() || {};
          const lead = data.leadData || {};
          const status = (data.status || "").toLowerCase();

          return {
            id: doc.id,
            name: lead.name || "N/A",
            email: lead.email || "N/A",
            phone: lead.phone || "N/A",
            source: data.source || "N/A",
            budget: lead.budget || "N/A",
            status,
            createdAt: data?.createdAt ? data.createdAt.toDate() : new Date(0), // Store as Date object
            date: data?.createdAt // Format date for display
              ? new Date(data.createdAt.toDate())
                  .toISOString()
                  .replace("T", " ")
                  .split(".")[0]
              : "N/A",
            avatar:
              (lead.name || "")
                .split(" ")
                .map((n) => n[0])
                .join("") || "NA",
            comment: data.comment || "",
            assignees: data.assignees || [], // Add assignees field
          };
        })
        // Sort in descending order to show latest on top
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setUsers(fetched);
      }
    );

    return () => unsubscribe();
  }, []);

  const getStatusBadge = (status) => {
    const map = {
      "à contacter": { color: "text-green-500", bg: "bg-green-500/10" },
      closé: { color: "text-gray-600", bg: "bg-gray-600/10" },
      "à rappeler": { color: "text-orange-500", bg: "bg-orange-500/10" },
      "Faux numéro": { color: "text-gray-400", bg: "bg-gray-700/30" },
      "pas intéressé": { color: "text-red-500", bg: "bg-red-500/10" },
      "Faux numéro": { color: "text-gray-400", bg: "bg-gray-700/30" }, // Using a visible gray text and slightly darker gray background
      "rdv booké": { color: "text-blue-500", bg: "bg-blue-500/10" },
      "follow-up": { color: "text-purple-500", bg: "bg-purple-500/10" },
      "no show": { color: "text-red-400", bg: "bg-red-400/10" },
    };
    const badge = map[status] || {
      color: "text-gray-400",
      bg: "bg-gray-600/10",
    };
    return (
      <span
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.color}`}
      >
        ● {status}
      </span>
    );
  };

  const getKanbanCloumntId = async (status) => {
    if (
      status === "closé" ||
      status === "a rappeler" ||
      status === "à contacter"
    ) {
      const collectionRef = collection(db, "kanban_columns");
      const q = query(collectionRef, where("status", "==", status));
      const columnId = (await getDocs(q)).docs[0]?.id;
      return columnId || "";
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const columnId = await getKanbanCloumntId(newStatus);
      const taskRef = doc(db, "kanban_tasks", id);
      
      if (columnId) {
        await updateDoc(taskRef, { status: newStatus, columnId: columnId });
      } else {
        await updateDoc(taskRef, { status: newStatus });
      }

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === id ? { ...user, status: newStatus } : user
        )
      );
      await updateDoc(taskRef, { status: newStatus });
      // The onSnapshot listener will automatically update the state,
      // so manually updating setUsers here might be redundant but safe.
      // setUsers((prevUsers) =>
      //   prevUsers.map((user) =>
      //     user.id === id ? { ...user, status: newStatus } : user
      //   )
      // );
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleLeadInfoModal = (lead) => {
    setSelectedLead(lead);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLead(null);
  };

  const handleDeleteLead = async (id) => {
    try {
      await deleteDoc(doc(db, "kanban_tasks", id));
      // onSnapshot will handle state update
      // setUsers(users.filter((user) => user.id !== id));
      console.log("Lead deleted successfully!");
    } catch (error) {
      console.error("Error deleting lead:", error);
    }
  };

  const handleAssignTask = (id) => {
    console.log("Assign task for lead:", id);
    // Logic to assign a task to a user
  };

  // Function to open the Add Lead modal
  const handleOpenAddLeadModal = () => {
    setIsAddLeadModalOpen(true);
  };

  // Function to close the Add Lead modal
  const handleCloseAddLeadModal = () => {
    setIsAddLeadModalOpen(false);
  };

  // Function to save a new lead to Firebase
  const handleSaveNewLead = async (newLeadData) => {
    try {
      const leadPayload = {
        leadData: {
          name: newLeadData.name,
          email: newLeadData.email,
          phone: newLeadData.phone,
          budget: newLeadData.budget,
        },
        source: newLeadData.source || "Tiktok",
        status: "à contacter",
        comment: newLeadData.comment,
        createdAt: serverTimestamp(), 
        assignees: [],
        columnId:"aXgSWRZhCnIQlZ9nThaw"
      };
      await addDoc(collection(db, "kanban_tasks"), leadPayload);
      console.log("New lead added successfully!");
    } catch (error) {
      console.error("Error adding new lead:", error);
    }
  };

  const columns = [
    {
      name: (
        <span className="flex gap-1 items-center">
          <img src={DateIcon} alt="date" />
          Date
        </span>
      ),
      selector: (row) => row.date,
      sortable: true,
      cell: (row) => (
        <span className="text-gray-300 text-xs text-start py-1">
          {row.date}
        </span>
      ),
      maxWidth: "120px",
    },
    {
      name: (
        <span className="flex gap-1 items-center">
          <img src={UserIcon} alt="user-icon" />
          Nom
        </span>
      ),
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="flex items-start gap-3 py-1 text-xs">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-xs font-medium text-white">
            {row.avatar}
          </div>
          <div>
            <div className="font-medium text-left text-white text-xs">
              {row.name}
            </div>
            <div className="text-gray-400 text-left text-xs">{row.email}</div>
            <div className="text-gray-400 text-left text-xs">{row.phone}</div>
          </div>
        </div>
      ),
      minWidth: "300px",
    },
    {
      name: (
        <span className="flex gap-1 items-center">
          <img src={SourceIcon} alt="source-icon" />
          Source
        </span>
      ),
      selector: (row) => row.source,
      sortable: true,
      cell: (row) => (
        <span className="text-gray-300 text-xs py-1">{row.source}</span>
      ),
      maxWidth: "100px",
    },
    {
      name: (
        <span className="flex gap-1 items-center">
          <img src={BudgetIcon} alt="budget-icon" />
          Budget
        </span>
      ),
      selector: (row) => row.budget,
      sortable: true,
      cell: (row) => (
        <span className="text-gray-300 text-left text-xs">{row.budget}</span>
      ),
      minWidth: "260px",
    },
    {
      name: (
        <span className="flex gap-1 items-center">
          <img src={MessageIcon} alt="message-icon" />
          Parcours
        </span>
      ),
      selector: (row) => row.comment,
      sortable: true,
      cell: (row) => (
        <span className="text-gray-300 text-left py-1 text-xs">
          {row.comment || "N/A"}
        </span>
      ),
    },
    {
      name: (
        <span className="flex gap-1 items-center">
          <img src={StatusIcon} alt="status" />
          Status
        </span>
      ),
      selector: (row) => row.status,
      cell: (row) => {
        const isEditing = editingStatusId === row.id;

        return isEditing ? (
          <select
            autoFocus
            value={row.status}
            onChange={(e) => {
              handleStatusChange(row.id, e.target.value);
              setEditingStatusId(null);
            }}
            onBlur={() => setEditingStatusId(null)}
            className="bg-[#081028] text-white border text-xs border-slate-600 px-2 py-1 rounded"
          >
            {statusOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <div
            className="cursor-pointer uppercase text-xs"
            onClick={() => setEditingStatusId(row.id)}
          >
            {getStatusBadge(row.status)}
          </div>
        );
      },
      minWidth: "200px",
    },
    {
      name: "",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <button
            className="p-1 bg-[#2563EB] text-xs flex gap-1 py-1 items-center text-white rounded transition-colors"
            onClick={() => setEditingStatusId(row.id)}
          >
            <Edit2 className="w-6 h-4 text-white text-xs hover:text-white" />
          </button>
          <button
            className="p-1 bg-[#2563EB] text-xs flex items-center gap-1 py-1 text-white rounded transition-colors"
            onClick={() => handleLeadInfoModal(row)}
          >
            <Info className="w-4 h-4 text-white text-xs hover:text-white" />
            <span>Information</span>
          </button>
        </div>
      ),
      minWidth: "250px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (searchTerm) {
      filtered = filtered.filter((user) =>
        [user.name, user.email, user.source, user.budget].some((val) =>
          val?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    if (assigneeFilter === "assignedToMe") {
      filtered = filtered.filter((user) =>
        user.assignees.includes(currentUserId)
      );
    }
    return filtered;
  }, [searchTerm, users, assigneeFilter, currentUserId]);

  const handleSelectedRowsChange = ({ selectedRows }) => {
    setSelectedRows(selectedRows);
  };

  const statusCounts = users.reduce((acc, user) => {
    const status = user.status;
    if (!acc[status]) acc[status] = 0;
    acc[status]++;
    return acc;
  }, {});

  const stats = [
    {
      title: "A rappeler",
      value: statusCounts["à rappeler"]?.toString() || "0",
      icon: Heart,
      color: "text-green-400",
      bgColor: "bg-green-400/10",
    },
    {
      title: "Follow up",
      value: statusCounts["follow-up"]?.toString() || "0",
      icon: Calendar,
      color: "text-yellow-400",
      bgColor: "bg-yellow-400/10",
    },
    {
      title: "Total Leads",
      value: users.length.toString(),
      icon: Users,
      color: "text-blue-400",
      bgColor: "bg-blue-400/10",
    },
  ];

  return (
    <div className="text-white overflow-auto scrollbar-thin">
      <Header
        PageName="Kanban"
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <div className="h-fit bg-[#081028] px-4 py-2">
        <div className="flex justify-between items-center mb-3">
          <h1 className="text-2xl font-semibold">Utilisateurs</h1>
          <div className="flex items-center gap-4">
            {/* Updated button to open AddLeadModal */}
            <button
              onClick={handleOpenAddLeadModal}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Ajouter un utilisateur
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-[#0b1739] rounded p-2 border border-slate-700"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div className="flex items-start justify-between w-full gap-10">
                    <div className="flex flex-col items-start">
                      <p className="text-white font-bold text-lg mb-1">
                        {stat.title}
                      </p>
                      <p className="font-semibold text-gray-400 text-[12px]">
                        Total :{" "}
                        <span className="font-normal">{stat.value}</span>
                      </p>
                    </div>
                    <div className="flex flex-col items-start">
                      <div className="font-semibold text-gray-400 text-[12px]">
                        Candidatures : <span className="font-normal">70</span>
                      </div>
                      <div className="font-semibold text-gray-400 text-[12px]">
                        Webinaire : <span className="font-normal">40</span>
                      </div>
                      <div className="font-semibold text-gray-400 text-[12px]">
                        Ads VSL : <span className="font-normal">12</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#0b1739] rounded border border-slate-700 overflow-hidden">
          <div className="p-4 py-2 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">All Users</h2>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-[#0b1739] border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-blue-500 w-64"
                  />
                </div>
                <select
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  className="bg-[#0b1739] border border-slate-700 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="all">All</option>
                  <option value="assignedToMe">Assign to me</option>
                </select>
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredUsers}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            dense
            onSelectedRowsChange={handleSelectedRowsChange}
            customStyles={leadsStyles}
            noDataComponent={
              <div className="bg-[#0b1739] w-full text-gray-400 py-3">
                Aucun utilisateur trouvé
              </div>
            }
            paginationComponentOptions={{
              rowsPerPageText: "Lignes par page:",
              rangeSeparatorText: "sur",
              noRowsPerPage: false,
              selectAllRowsItem: false,
            }}
          />
        </div>
      </div>

      <LeadInfoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        leadData={selectedLead}
        onUpdateStatus={handleStatusChange}
        onDeleteLead={handleDeleteLead}
        onAssignTask={handleAssignTask}
      />

      {/* New AddLeadModal component */}
      <AddLeadModal
        isOpen={isAddLeadModalOpen}
        onClose={handleCloseAddLeadModal}
        onSave={handleSaveNewLead}
      />
    </div>
  );
};

export default Leads;